import { useCallback, useMemo, useReducer } from 'react';

export type StateType = {
  maxIndex: number;
  activeIndex: number;
  prevDisabled: boolean;
  nextDisabled: boolean;
};

type ActionType =
  | { type: 'activeIndex'; payload: number }
  | { type: 'clickPrevious' | 'clickNext'; payload?: never };

const tabsReducer = (state: StateType, action: ActionType) => {
  switch (action.type) {
    case 'activeIndex':
      return {
        ...state,
        activeIndex: action.payload,
        prevDisabled: action.payload === 0,
        nextDisabled: action.payload === state.maxIndex,
      };

    case 'clickPrevious': {
      const activeIndex = state.activeIndex > 0 ? state.activeIndex - 1 : state.activeIndex;
      return {
        ...state,
        activeIndex,
        prevDisabled: activeIndex === 0,
        nextDisabled: activeIndex === state.maxIndex,
      };
    }

    case 'clickNext': {
      const activeIndex =
        state.activeIndex < state.maxIndex ? state.activeIndex + 1 : state.activeIndex;
      return {
        ...state,
        activeIndex,
        prevDisabled: activeIndex === 0,
        nextDisabled: activeIndex === state.maxIndex,
      };
    }
  }
};

export type UseTabsProps = {
  numberOfItems: number;
  initialIndex?: number;
};

const useTabs = ({ numberOfItems, initialIndex }: UseTabsProps) => {
  const [state, dispatch] = useReducer(tabsReducer, {
    maxIndex: numberOfItems - 1,
    activeIndex: initialIndex || 0,
    prevDisabled: true,
    nextDisabled: false,
  });

  const setActiveIndex = useCallback(
    (index: number) => dispatch({ type: 'activeIndex', payload: index }),
    []
  );

  return useMemo(
    () => ({
      activeIndex: state.activeIndex,
      setActiveIndex,
      handlePrevClick: () => dispatch({ type: 'clickPrevious' }),
      handleNextClick: () => dispatch({ type: 'clickNext' }),
      getMobileLabel: (index: number) => `${index + 1}/${numberOfItems}`,
      isPrevArrowDisabled: state.prevDisabled,
      isNextArrowDisabled: state.nextDisabled,
    }),
    [state, dispatch, numberOfItems, setActiveIndex]
  );
};

export default useTabs;
